import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  Switch,
  Button,
  Divider,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  ImageOutlined,
  AnalyticsOutlined,
  EventOutlined,
  PersonAddOutlined,
  LockOutlined,
  MenuBookOutlined,
  LinkOutlined,
  DashboardOutlined,
  SchoolOutlined,
  SettingsSuggestOutlined,
  EmailOutlined,
  OpenInNew,
  GroupsOutlined,
  CampaignOutlined,
  DateRangeOutlined,
} from "@mui/icons-material";
import TrackChangesOutlined from "@mui/icons-material/TrackChangesOutlined";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LoadingButton } from "@mui/lab";
import LanguageIcon from "@mui/icons-material/Language";
import axios from "axios";
import { toast } from "react-toastify";

import MyQuill from "../quill/MyQuill";
import { ADMIN, API_ENDPOINT } from "../utils/constants";
import { toastError, validateEmail } from "../utils/util";
import useApiEp from "../hooks/useApiEp";
import { getHeader } from "../utils/utils2";

const SettingsPage = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/admin/get-event-settings`
  );

  const [senderEmail, setSenderEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  // Separate loading states
  const [savingCoreSettings, setSavingCoreSettings] = useState(false);
  const [savingFormSettings, setSavingFormSettings] = useState(false);

  const [settings, setSettings] = useState({
    // Core Settings
    eventName: "",
    eventStartDate: null,
    eventEndDate: null,
    userDashLink: "",
    visitorRegLink: "",
    studentRegLink: "",
    disableExhibitionCatalogue: false,
    exhibitionCatalogueMessage: "",
    exhibitionCatalogueExpiryDate: null, // New field

    // Forms & Banner Ads Settings
    visitorRegStartDate: null,
    visitorRegEndDate: null,
    tdStartDate: null,
    tdEndDate: null,
    disableVisitorReg: false,
    disableTradeDelegateReg: false,
    visitorRegMessage: "",
    tradeDelegateRegMessage: "",
    showBannerAdsVisitorReg: false,
    showBannerAdsTradeDelegateReg: false,
  });

  useEffect(() => {
    if (data) {
      if (data.emailTemplate?.senderEmail) {
        setSenderEmail(data.emailTemplate.senderEmail);
      }

      setSettings((prev) => ({
        ...prev,
        eventName: data?.eventName ?? "",
        eventStartDate: data.eventStartDate
          ? new Date(data.eventStartDate)
          : null,
        eventEndDate: data.eventEndDate ? new Date(data.eventEndDate) : null,
        userDashLink: data?.userDashLink ?? "",
        visitorRegLink: data?.visitorRegLink ?? "",
        studentRegLink: data?.studentRegLink ?? "",
        disableExhibitionCatalogue:
          data?.disableExhibitionCatalogue === "true" ||
          data?.disableExhibitionCatalogue === true,
        exhibitionCatalogueMessage: data?.exhibitionCatalogueMessage ?? "",
        exhibitionCatalogueExpiryDate: data.exhibitionCatalogueExpiryDate
          ? new Date(data.exhibitionCatalogueExpiryDate)
          : null, // New field mapped

        visitorRegStartDate: data.visitorRegStartDate
          ? new Date(data.visitorRegStartDate)
          : null,
        visitorRegEndDate: data.visitorRegEndDate
          ? new Date(data.visitorRegEndDate)
          : null,
        tdStartDate: data.tdStartDate ? new Date(data.tdStartDate) : null,
        tdEndDate: data.tdEndDate ? new Date(data.tdEndDate) : null,
        disableVisitorReg: data?.disableVisitorReg === true,
        disableTradeDelegateReg: data?.disableTradeDelegateReg === true,
        visitorRegMessage: data?.visitorRegMessage ?? "",
        tradeDelegateRegMessage: data?.tradeDelegateRegMessage ?? "",
        showBannerAdsVisitorReg: data?.showBannerAdsVisitorReg === true,
        showBannerAdsTradeDelegateReg:
          data?.showBannerAdsTradeDelegateReg === true,
      }));
    }
  }, [data]);

  const handleDateChange = (field) => (date) =>
    setSettings((prev) => ({ ...prev, [field]: date }));
  const handleSwitchChange = (field) => (event) =>
    setSettings((prev) => ({ ...prev, [field]: event.target.checked }));
  const handleMessageChange = (field) => (newValue) =>
    setSettings((prev) => ({ ...prev, [field]: newValue }));
  const handleLinkChange = (field) => (event) =>
    setSettings((prev) => ({ ...prev, [field]: event.target.value }));

  const validateCoreSettings = () => {
    if (
      settings.disableExhibitionCatalogue &&
      !settings.exhibitionCatalogueMessage.trim()
    ) {
      toast.warning(
        "Please provide a message for disabled exhibition catalogue"
      );
      return false;
    }
    return true;
  };

  const validateFormSettings = () => {
    if (settings.disableVisitorReg && !settings.visitorRegMessage.trim()) {
      toast.warning(
        "Please provide a message for disabled visitor registration"
      );
      return false;
    }
    if (
      settings.disableTradeDelegateReg &&
      !settings.tradeDelegateRegMessage.trim()
    ) {
      toast.warning(
        "Please provide a message for disabled trade delegate registration"
      );
      return false;
    }
    return true;
  };

  const handleCoreSubmit = async () => {
    if (!validateCoreSettings()) return;
    try {
      setSavingCoreSettings(true);
      const corePayload = {
        eventName: settings.eventName,
        eventStartDate: settings.eventStartDate
          ? settings.eventStartDate.toISOString()
          : null,
        eventEndDate: settings.eventEndDate
          ? settings.eventEndDate.toISOString()
          : null,
        userDashLink: settings.userDashLink,
        visitorRegLink: settings.visitorRegLink,
        studentRegLink: settings.studentRegLink,
        disableExhibitionCatalogue: settings.disableExhibitionCatalogue,
        exhibitionCatalogueMessage: settings.exhibitionCatalogueMessage,
        exhibitionCatalogueExpiryDate: settings.exhibitionCatalogueExpiryDate
          ? settings.exhibitionCatalogueExpiryDate.toISOString()
          : null, // Added to payload
      };

      const { data: responseData } = await axios.put(
        `${API_ENDPOINT}/admin/save-settings`,
        corePayload,
        getHeader()
      );
      toast.success(responseData.message || "Core settings saved successfully");
    } catch (error) {
      toastError(error);
    } finally {
      setSavingCoreSettings(false);
    }
  };

  const handleFormAdSubmit = async () => {
    if (!validateFormSettings()) return;
    try {
      setSavingFormSettings(true);
      const formAdPayload = {
        visitorRegStartDate: settings.visitorRegStartDate
          ? settings.visitorRegStartDate.toISOString()
          : null,
        visitorRegEndDate: settings.visitorRegEndDate
          ? settings.visitorRegEndDate.toISOString()
          : null,
        tdStartDate: settings.tdStartDate
          ? settings.tdStartDate.toISOString()
          : null,
        tdEndDate: settings.tdEndDate ? settings.tdEndDate.toISOString() : null,
        disableVisitorReg: settings.disableVisitorReg,
        disableTradeDelegateReg: settings.disableTradeDelegateReg,
        visitorRegMessage: settings.visitorRegMessage,
        tradeDelegateRegMessage: settings.tradeDelegateRegMessage,
        showBannerAdsVisitorReg: settings.showBannerAdsVisitorReg,
        showBannerAdsTradeDelegateReg: settings.showBannerAdsTradeDelegateReg,
      };

      const { data: responseData } = await axios.put(
        `${API_ENDPOINT}/admin/save-form-ad-settings`,
        formAdPayload,
        getHeader()
      );
      toast.success(
        responseData.message || "Forms & Ads settings saved successfully"
      );
    } catch (error) {
      toastError(error);
    } finally {
      setSavingFormSettings(false);
    }
  };

  const handleEmailSubmit = async () => {
    try {
      setSavingEmail(true);
      const { data: responseData } = await axios.post(
        `${API_ENDPOINT}/admin/update-sender-email`,
        { email: senderEmail },
        getHeader(ADMIN)
      );
      refetch();
      toast.success(responseData.message || "Email updated successfully");
    } catch (error) {
      toastError(error);
    } finally {
      setSavingEmail(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography
        variant="h4"
        gutterBottom
        fontWeight="bold"
        sx={{ mb: 4, display: "flex", alignItems: "center", gap: 1 }}
      >
        <SettingsSuggestOutlined fontSize="large" color="primary" />
        Application Settings
      </Typography>

      <Grid container spacing={4} sx={{ alignItems: "flex-start" }}>
        {/* Left Column: Quick Actions & Email Configuration (Fixed/Sticky on Scroll) */}
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            position: { md: "sticky" },
            top: { md: "90px" },
            alignSelf: { md: "flex-start" },
            zIndex: 10,
            maxHeight: { md: "calc(100vh - 110px)" },
            overflowY: { md: "auto" },
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
          }}
        >
          <Card elevation={2} sx={{ mb: 4 }}>
            <CardHeader
              title="Quick Actions"
              titleTypographyProps={{ variant: "h6" }}
            />
            <Divider />
            <CardContent
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ImageOutlined />}
                onClick={() => navigate("/banner-logo-settings")}
                sx={{ justifyContent: "flex-start" }}
              >
                Banner & Logo
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<LanguageIcon />}
                onClick={() => navigate("/meta-data-editor")}
                sx={{ justifyContent: "flex-start" }}
              >
                App Site Metadata
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AnalyticsOutlined />}
                onClick={() => navigate("/update-gtag")}
                sx={{ justifyContent: "flex-start" }}
              >
                Gtag Analytics
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AnalyticsOutlined />}
                onClick={() => navigate("/update-meta-pixel-tag")}
                sx={{ justifyContent: "flex-start" }}
              >
                Meta Pixel Analytics
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<TrackChangesOutlined />}
                onClick={() =>
                  navigate("/registration-analytics-tracker/template1")
                }
                sx={{ justifyContent: "flex-start" }}
              >
                Registration Tracker
              </Button>
            </CardContent>
          </Card>

          <Card elevation={2}>
            <CardHeader
              title="Email Configuration"
              titleTypographyProps={{ variant: "h6" }}
              avatar={<EmailOutlined color="action" />}
            />
            <Divider />
            <CardContent>
              <TextField
                fullWidth
                value={senderEmail}
                error={Boolean(senderEmail && !validateEmail(senderEmail))}
                helperText={
                  senderEmail && !validateEmail(senderEmail)
                    ? "Invalid email address"
                    : ""
                }
                onChange={(e) => setSenderEmail(e.target.value)}
                variant="outlined"
                size="small"
                label="Global Sender Email"
                sx={{ mb: 2 }}
              />
              <LoadingButton
                fullWidth
                loading={savingEmail}
                onClick={handleEmailSubmit}
                variant="contained"
                color="secondary"
                disabled={!senderEmail || !validateEmail(senderEmail)}
              >
                Update Email
              </LoadingButton>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column: Settings Forms */}
        <Grid item xs={12} md={8}>
          {/* ======================= */}
          {/*     CORE SETTINGS       */}
          {/* ======================= */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h5"
              color="primary"
              sx={{ mb: 2, fontWeight: 600 }}
            >
              Core Information & Catalog
            </Typography>

            <Card elevation={2} sx={{ mb: 3 }}>
              <CardHeader
                title="Event Details"
                titleTypographyProps={{ variant: "h6" }}
                avatar={<EventOutlined color="action" />}
              />
              <Divider />
              <CardContent
                sx={{ display: "flex", flexDirection: "column", gap: 3 }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Event Name"
                  placeholder="e.g. Tech Expo 2026"
                  value={settings.eventName}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      eventName: e.target.value,
                    }))
                  }
                  variant="outlined"
                />

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                    }}
                  >
                    <DatePicker
                      label="Event Start Date"
                      value={settings.eventStartDate}
                      onChange={handleDateChange("eventStartDate")}
                      slotProps={{
                        textField: { fullWidth: true, size: "small" },
                      }}
                    />
                    <DatePicker
                      label="Event End Date"
                      value={settings.eventEndDate}
                      onChange={handleDateChange("eventEndDate")}
                      slotProps={{
                        textField: { fullWidth: true, size: "small" },
                      }}
                    />
                  </Box>
                </LocalizationProvider>
              </CardContent>
            </Card>

            <Card elevation={2} sx={{ mb: 3 }}>
              <CardHeader
                title="Exhibition Catalogue"
                titleTypographyProps={{ variant: "h6" }}
                avatar={<MenuBookOutlined color="action" />}
              />
              <Divider />
              <CardContent
                sx={{ display: "flex", flexDirection: "column", gap: 3 }}
              >
                <Box
                  sx={{ p: 2, bgcolor: "background.default", borderRadius: 1 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: settings.disableExhibitionCatalogue ? 2 : 2,
                    }}
                  >
                    <MenuBookOutlined sx={{ mr: 2, color: "text.secondary" }} />
                    <Typography
                      variant="body1"
                      sx={{ flexGrow: 1, fontWeight: 500 }}
                    >
                      Disable Exhibition Catalogue
                    </Typography>
                    <Switch
                      checked={settings.disableExhibitionCatalogue}
                      onChange={handleSwitchChange(
                        "disableExhibitionCatalogue"
                      )}
                      color="warning"
                    />
                  </Box>

                  {settings.disableExhibitionCatalogue && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        gutterBottom
                      >
                        Message to display to users:
                      </Typography>
                      <MyQuill
                        value={settings.exhibitionCatalogueMessage}
                        onChange={handleMessageChange(
                          "exhibitionCatalogueMessage"
                        )}
                      />
                    </Box>
                  )}

                  {/* Added Expiry Date Picker */}
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Catalogue Expiry Date"
                      value={settings.exhibitionCatalogueExpiryDate}
                      onChange={handleDateChange(
                        "exhibitionCatalogueExpiryDate"
                      )}
                      slotProps={{
                        textField: { fullWidth: true, size: "small" },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              </CardContent>
            </Card>

            <Card elevation={2} sx={{ mb: 3 }}>
              <CardHeader
                title="External Routing Links"
                titleTypographyProps={{ variant: "h6" }}
                avatar={<LinkOutlined color="action" />}
              />
              <Divider />
              <CardContent
                sx={{ display: "flex", flexDirection: "column", gap: 3 }}
              >
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    label="User Dashboard Link"
                    value={settings.userDashLink}
                    onChange={handleLinkChange("userDashLink")}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <DashboardOutlined
                          sx={{ mr: 1.5, color: "action.active" }}
                        />
                      ),
                      endAdornment: settings.userDashLink ? (
                        <InputAdornment position="end">
                          {" "}
                          <IconButton
                            size="small"
                            onClick={() =>
                              window.open(
                                settings.userDashLink,
                                "_blank",
                                "noopener,noreferrer"
                              )
                            }
                          >
                            {" "}
                            <OpenInNew fontSize="small" />{" "}
                          </IconButton>{" "}
                        </InputAdornment>
                      ) : undefined,
                    }}
                  />
                </Box>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    label="Visitor Registration Link"
                    value={settings.visitorRegLink}
                    onChange={handleLinkChange("visitorRegLink")}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <PersonAddOutlined
                          sx={{ mr: 1.5, color: "action.active" }}
                        />
                      ),
                      endAdornment: settings.visitorRegLink ? (
                        <InputAdornment position="end">
                          {" "}
                          <IconButton
                            size="small"
                            onClick={() =>
                              window.open(
                                settings.visitorRegLink,
                                "_blank",
                                "noopener,noreferrer"
                              )
                            }
                          >
                            {" "}
                            <OpenInNew fontSize="small" />{" "}
                          </IconButton>{" "}
                        </InputAdornment>
                      ) : undefined,
                    }}
                  />
                </Box>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    label="Student Registration Link"
                    value={settings.studentRegLink}
                    onChange={handleLinkChange("studentRegLink")}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <SchoolOutlined
                          sx={{ mr: 1.5, color: "action.active" }}
                        />
                      ),
                      endAdornment: settings.studentRegLink ? (
                        <InputAdornment position="end">
                          {" "}
                          <IconButton
                            size="small"
                            onClick={() =>
                              window.open(
                                settings.studentRegLink,
                                "_blank",
                                "noopener,noreferrer"
                              )
                            }
                          >
                            {" "}
                            <OpenInNew fontSize="small" />{" "}
                          </IconButton>{" "}
                        </InputAdornment>
                      ) : undefined,
                    }}
                  />
                </Box>
              </CardContent>
            </Card>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <LoadingButton
                variant="contained"
                color="primary"
                size="large"
                onClick={handleCoreSubmit}
                loading={savingCoreSettings}
                sx={{ px: 4, py: 1 }}
              >
                Save Core Settings
              </LoadingButton>
            </Box>
          </Box>

          {/* ============================== */}
          {/*   FORMS & BANNER ADS SETTINGS  */}
          {/* ============================== */}
          <Box>
            <Typography
              variant="h5"
              color="secondary"
              sx={{ mb: 2, fontWeight: 600 }}
            >
              Forms & Ads Configuration
            </Typography>

            <Card elevation={2} sx={{ mb: 3 }}>
              <CardHeader
                title="Registration Dates"
                titleTypographyProps={{ variant: "h6" }}
                avatar={<DateRangeOutlined color="action" />}
              />
              <Divider />
              <CardContent
                sx={{ display: "flex", flexDirection: "column", gap: 3 }}
              >
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                    }}
                  >
                    <DatePicker
                      label="Visitor Reg. Start"
                      value={settings.visitorRegStartDate}
                      onChange={handleDateChange("visitorRegStartDate")}
                      slotProps={{
                        textField: { fullWidth: true, size: "small" },
                      }}
                    />
                    <DatePicker
                      label="Visitor Reg. End"
                      value={settings.visitorRegEndDate}
                      onChange={handleDateChange("visitorRegEndDate")}
                      slotProps={{
                        textField: { fullWidth: true, size: "small" },
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                    }}
                  >
                    <DatePicker
                      label="Trade Delegation Start"
                      value={settings.tdStartDate}
                      onChange={handleDateChange("tdStartDate")}
                      slotProps={{
                        textField: { fullWidth: true, size: "small" },
                      }}
                    />
                    <DatePicker
                      label="Trade Delegation End"
                      value={settings.tdEndDate}
                      onChange={handleDateChange("tdEndDate")}
                      slotProps={{
                        textField: { fullWidth: true, size: "small" },
                      }}
                    />
                  </Box>
                </LocalizationProvider>
              </CardContent>
            </Card>

            <Card elevation={2} sx={{ mb: 3 }}>
              <CardHeader
                title="Enable / Disable Registrations"
                titleTypographyProps={{ variant: "h6" }}
                avatar={<LockOutlined color="action" />}
              />
              <Divider />
              <CardContent
                sx={{ display: "flex", flexDirection: "column", gap: 3 }}
              >
                <Box
                  sx={{ p: 2, bgcolor: "background.default", borderRadius: 1 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: settings.disableVisitorReg ? 2 : 0,
                    }}
                  >
                    <PersonAddOutlined
                      sx={{ mr: 2, color: "text.secondary" }}
                    />
                    <Typography
                      variant="body1"
                      sx={{ flexGrow: 1, fontWeight: 500 }}
                    >
                      Disable Visitor Registration
                    </Typography>
                    <Switch
                      checked={settings.disableVisitorReg}
                      onChange={handleSwitchChange("disableVisitorReg")}
                      color="warning"
                    />
                  </Box>
                  {settings.disableVisitorReg && (
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        gutterBottom
                      >
                        Message to display to users:
                      </Typography>
                      <MyQuill
                        value={settings.visitorRegMessage}
                        onChange={handleMessageChange("visitorRegMessage")}
                      />
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{ p: 2, bgcolor: "background.default", borderRadius: 1 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: settings.disableTradeDelegateReg ? 2 : 0,
                    }}
                  >
                    <GroupsOutlined sx={{ mr: 2, color: "text.secondary" }} />
                    <Typography
                      variant="body1"
                      sx={{ flexGrow: 1, fontWeight: 500 }}
                    >
                      Disable Trade Delegate Registration
                    </Typography>
                    <Switch
                      checked={settings.disableTradeDelegateReg}
                      onChange={handleSwitchChange("disableTradeDelegateReg")}
                      color="warning"
                    />
                  </Box>
                  {settings.disableTradeDelegateReg && (
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        gutterBottom
                      >
                        Message to display to users:
                      </Typography>
                      <MyQuill
                        value={settings.tradeDelegateRegMessage}
                        onChange={handleMessageChange(
                          "tradeDelegateRegMessage"
                        )}
                      />
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>

            <Card elevation={2} sx={{ mb: 3 }}>
              <CardHeader
                title="Banner Ads Visibility"
                titleTypographyProps={{ variant: "h6" }}
                avatar={<CampaignOutlined color="action" />}
              />
              <Divider />
              <CardContent
                sx={{ display: "flex", flexDirection: "column", gap: 2 }}
              >
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "background.default",
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <PersonAddOutlined sx={{ mr: 2, color: "text.secondary" }} />
                  <Typography
                    variant="body1"
                    sx={{ flexGrow: 1, fontWeight: 500 }}
                  >
                    Show Banner Ads on Visitor Registration
                  </Typography>
                  <Switch
                    checked={settings.showBannerAdsVisitorReg}
                    onChange={handleSwitchChange("showBannerAdsVisitorReg")}
                    color="secondary"
                  />
                </Box>

                <Box
                  sx={{
                    p: 2,
                    bgcolor: "background.default",
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <GroupsOutlined sx={{ mr: 2, color: "text.secondary" }} />
                  <Typography
                    variant="body1"
                    sx={{ flexGrow: 1, fontWeight: 500 }}
                  >
                    Show Banner Ads on Trade Delegate Registration
                  </Typography>
                  <Switch
                    checked={settings.showBannerAdsTradeDelegateReg}
                    onChange={handleSwitchChange(
                      "showBannerAdsTradeDelegateReg"
                    )}
                    color="secondary"
                  />
                </Box>
              </CardContent>
            </Card>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <LoadingButton
                variant="contained"
                color="secondary"
                size="large"
                onClick={handleFormAdSubmit}
                loading={savingFormSettings}
                sx={{ px: 4, py: 1 }}
              >
                Save Forms & Ads Settings
              </LoadingButton>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SettingsPage;
